import { PropType } from 'vue';

import getIn from 'lodash/get';
import { Component, Prop, Vue, Mixins } from 'vue-property-decorator';

import JsonForm from '@evidentid/json-schema/interfaces/JsonForm';
import { pickListenerAttrs } from '@evidentid/vue-commons/utils/attrsListeners';

import { defaultStrings } from '../defaultStrings';
import { JsonSchemaFormCustomComponentInput, JsonSchemaFormStrings } from '../types';

export type CustomValidate<T = any> = (value: T) => boolean;

@Component({
    emits: [ 'blur', 'focus', 'touch' ],
})
class AbstractJsonSchemaForm<T extends JsonForm = JsonForm, V = any> extends Vue {
    @Prop(Object)
    protected form!: T;

    @Prop()
    protected value!: V;

    @Prop({ type: Boolean, default: false })
    protected required!: boolean;

    @Prop({ type: Boolean, default: false })
    protected disabled!: boolean;

    @Prop({ type: Boolean, default: false })
    protected touched!: boolean;

    @Prop({ type: Boolean, default: false })
    protected deletable!: boolean;

    @Prop(String)
    protected id!: string;

    @Prop({ type: Number, default: 0 })
    protected depth!: number;

    // PRODUCT-26110 Phase 2B2: renamed from PascalCase `FormElementComponent`. Vue 2
    // matched the kebab-case attr (`:form-element-component=`) to a PascalCase prop via
    // hyphenate(); Vue 3 camelizes the attr instead (-> `formElementComponent`), so the
    // PascalCase prop never received a value and recursive object/array forms rendered
    // `<component :is="undefined">` -- an empty form body.
    // PRODUCT-26110 QA fixes4: the value passed in is a component definition OBJECT
    // (an options object / defineComponent result), not a plain `Function` -- `type:
    // Function` only matches function/class components and fails Vue 3's prop-type
    // check for the common object-literal case. Accept both.
    @Prop({ type: [ Function, Object ] as PropType<Vue.Component> })
    protected formElementComponent!: Vue.Component;

    @Prop({ type: [ Object, Function ] as PropType<JsonSchemaFormCustomComponentInput | null> })
    private customComponentInput?: JsonSchemaFormCustomComponentInput | null;

    @Prop({ type: Object, default: () => ({}) })
    protected strings!: Partial<JsonSchemaFormStrings>;

    @Prop({ type: String, default: '' })
    protected customTitle!: string;

    @Prop({ type: String, default: '' })
    protected description!: string;

    @Prop({ type: Boolean, default: false })
    protected rbacDisabled!: boolean;

    @Prop({ type: Boolean, default: false })
    protected valueOnly!: boolean;

    @Prop({ type: Boolean, default: false })
    protected wrapValueMode!: boolean;

    @Prop({ type: Boolean, default: false })
    protected noLocalTouch!: boolean;

    protected localTouched = false;
    protected focused = false;

    // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): subclasses forward `v-bind="$props"`
    // (declared props only, deliberately excluding raw attrs -- `inheritAttrs: false`)
    // plus listeners down to a nested element. Vue 3 merges `$listeners` into `$attrs`,
    // so a blanket `v-bind="$attrs"` here would newly leak raw attrs that were never
    // forwarded before -- pick just the `onXxx` (listener) keys back out instead.
    protected get listenerAttrs(): Record<string, unknown> {
        return pickListenerAttrs(this.$attrs);
    }

    // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): Vue's template compiler rejects two
    // bare `v-bind` directives on the same element ("Duplicate attribute"), so
    // `v-bind="$props"` and `v-bind="listenerAttrs"` must be merged into one object
    // wherever a subclass previously wrote `v-bind="$props" v-on="$listeners"`.
    protected get propsWithListenerAttrs(): Record<string, unknown> {
        return { ...this.$props, ...this.listenerAttrs };
    }

    protected get empty(): boolean {
        return this.form.isEmpty(this.form.getValue(this.value), true);
    }

    protected get valid(): boolean {
        return this.form.isValid(this.form.getValue(this.value), true);
    }

    protected get accessed(): boolean {
        return this.touched || this.localTouched;
    }

    protected touch(): void {
        if (!this.noLocalTouch) {
            this.localTouched = true;
        }
        this.focused = false;
        this.$emit('touch');
        this.$emit('blur');
    }

    protected onFocus(): void {
        this.focused = true;
        this.$emit('focus');
    }

    protected get hasError(): boolean {
        return Boolean(this.error);
    }

    protected get error(): string | null {
        if (this.empty && this.required && !this.valid) {
            return this.translate('error.required');
        } else if (this.empty) {
            return null;
        }
        return this.valid ? null : this.getCustomError() || this.translate('error.unknown');
    }

    protected get TypeLabelClass(): string | null {
        return this.accessed && this.error
            ? 'JsonSchemaForm__typeLabel--error'
            : this.focused
                ? 'JsonSchemaForm__typeLabel--focused'
                : null;
    }

    protected getCustomError(): string | null {
        return null;
    }

    protected get schemaStrings(): Partial<JsonSchemaFormStrings> {
        return this.form.schema.strings || {};
    }

    protected translate(phrase: keyof JsonSchemaFormStrings, data: any = {}): string {
        const finalData = { ...data, schema: this.form.schema };
        const baseString = this.schemaStrings[phrase] || this.strings[phrase] || defaultStrings[phrase] || phrase;
        return baseString.replace(/{([^}]+)}/g, (_: string, key: string) => `${getIn(finalData, key)}`);
    }
}

const AbstractJsonSchemaFormMixin = Mixins(AbstractJsonSchemaForm) as typeof AbstractJsonSchemaForm;
export default AbstractJsonSchemaFormMixin;
